import { useEffect, useState, type ReactNode } from 'react';
import { fetchMe, logout as apiLogout } from '@/api/auth';
import { AuthContext, type AuthState } from './context';
export type { AuthState, AuthContextValue } from './context';

/*
  AuthProvider грузит /auth/me один раз при старте и держит User в state.
  Дочерние компоненты читают через useAuth(). При logout — чистит state
  и редиректит на /login.

  Намеренно простая реализация — без react-query поверх, потому что
  это базовый bootstrap, его дёргает ровно один компонент один раз.
  Когда понадобится refresh user-state по событию (например после
  обновления профиля в Settings) — переедем на useQuery.
*/

async function readAuthState(): Promise<AuthState> {
  try {
    const user = await fetchMe();
    return user ? { status: 'authenticated', user } : { status: 'anonymous' };
  } catch (err) {
    return {
      status: 'error',
      error: err instanceof Error ? err : new Error(String(err)),
    };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = async () => {
    setState(await readAuthState());
  };

  const logout = async () => {
    try {
      await apiLogout();
    } finally {
      setState({ status: 'anonymous' });
      // Hard redirect, чтобы стереть любое локальное состояние.
      window.location.assign('/login');
    }
  };

  useEffect(() => {
    void readAuthState().then(setState);
  }, []);

  return <AuthContext.Provider value={{ state, logout, refresh }}>{children}</AuthContext.Provider>;
}
